import {
  FormOutlined,
  RadarChartOutlined,
  UserOutlined,
} from '@ant-design/icons/lib';
import React, { FC, memo } from 'react';
import { MenuEx, MenuPropsEx } from 'common/components/MenuEx';
import { BusinessPartnerRoutes } from 'pages/BusinessPartnerManagement/businessPartnerRoutes';
import { useFormatMessage, IntlMessage } from 'common/hooks/useFormatMessage';
import { useSelector } from 'react-redux';
import {
  needMfaEnabledSelector,
  tokenPayloadSelector,
} from 'state/auth/auth.selectors';
import { useQuery } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { useRole } from 'common/hooks/useRole';
import { isNil } from 'ramda';

const buildMenuItems = (intlMessage: IntlMessage) => [
  {
    key: 'details',
    icon: <FormOutlined />,
    text: intlMessage({ id: 'details' }),
    route: `/details`,
  },
  {
    key: 'devices',
    icon: <RadarChartOutlined />,
    text: intlMessage({ id: 'devices' }),
    route: `/devices`,
  },
  {
    key: 'my-partners',
    icon: <UserOutlined />,
    text: intlMessage({ id: 'myPartners' }),
    route: `/my-partners`,
  },
  {
    key: 'users-management',
    icon: <UserOutlined />,
    text: intlMessage({ id: 'users' }),
    children: [
      {
        key: 'users-management/admins',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'admins' }),
        route: `/${BusinessPartnerRoutes.Admins}`,
      },
      {
        key: 'users-management/doctors',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'doctors' }),
        route: `/${BusinessPartnerRoutes.Doctors}`,
      },
      {
        key: 'users-management/users',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'familyUsers' }),
        route: `/${BusinessPartnerRoutes.FamilyUsers}`,
      },
      {
        key: 'users-management/medical-operators',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'medicalOperators' }),
        route: `/${BusinessPartnerRoutes.MedicalOperators}`,
      },
      {
        key: 'users-management/patients',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'patients' }),
        route: `/${BusinessPartnerRoutes.Patients}`,
      },
    ],
  },
];

const buildMenuItemsWithoutMyPartners = (intlMessage: IntlMessage) => [
  {
    key: 'details',
    icon: <FormOutlined />,
    text: intlMessage({ id: 'details' }),
    route: `/details`,
  },
  {
    key: 'devices',
    icon: <RadarChartOutlined />,
    text: intlMessage({ id: 'devices' }),
    route: `/devices`,
  },
  {
    key: 'users-management',
    icon: <UserOutlined />,
    text: intlMessage({ id: 'users' }),
    children: [
      {
        key: 'users-management/admins',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'admins' }),
        route: `/${BusinessPartnerRoutes.Admins}`,
      },
      {
        key: 'users-management/doctors',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'doctors' }),
        route: `/${BusinessPartnerRoutes.Doctors}`,
      },
      {
        key: 'users-management/users',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'familyUsers' }),
        route: `/${BusinessPartnerRoutes.FamilyUsers}`,
      },
      {
        key: 'users-management/medical-operators',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'medicalOperators' }),
        route: `/${BusinessPartnerRoutes.MedicalOperators}`,
      },
      {
        key: 'users-management/patients',
        icon: <UserOutlined />,
        text: intlMessage({ id: 'patients' }),
        route: `/${BusinessPartnerRoutes.Patients}`,
      },
    ],
  },
];

export interface BusinessPartnerModuleMenuProps
  extends Omit<MenuPropsEx, 'menuItems'> {
  className?: string;
}

export const BusinessPartnerModuleMenu: FC<BusinessPartnerModuleMenuProps> = memo(
  (props) => {
    const intlMessage = useFormatMessage();

    const tokenPayload = useSelector(tokenPayloadSelector)!;
    const needMfaEnabled = useSelector(needMfaEnabledSelector);
    const businessPartnerId = tokenPayload.partnerId!;
    const { isMedicalOperator, isSuperAdmin } = useRole();

    const { isLoading, data } = useQuery(
      [QueryKey.BusinessPartner, businessPartnerId],
      () => {
        if (!isSuperAdmin && !needMfaEnabled) {
          return BusinessPartnersAPI.fetchById(businessPartnerId);
        }
      }
    );

    const shouldHideMyPartnerPage =
      (!isLoading && !isNil(data?.parentPartnerId)) || isMedicalOperator;

    return (
      <MenuEx
        {...props}
        theme="light"
        mode="inline"
        menuItems={
          isSuperAdmin || !shouldHideMyPartnerPage
            ? buildMenuItems(intlMessage)
            : buildMenuItemsWithoutMyPartners(intlMessage)
        }
      />
    );
  }
);
